import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// HTML react parser
import parse, { attributesToProps, domToReact } from "html-react-parser";

// Helpers
import { absoluteUrl, isoDate, plainText } from "../../helpers/seo";

// Components
import Seo, { organizationRef } from "../../components/Seo";
import ContainerText from "../../components/Container";
import Footer from "../../components/Footer";
import Header from "../../components/Header";

// Images
import RegisterIcon from "../../images/icons/RegisterIcon.png";
import WhatsappIcon from "../../images/icons/WhatsappIcon.png";
import ArrowDownIcon from "../../images/icons/ArrowDownIcon.png";
import ArrowUpIcon from "../../images/icons/ArrowUpIcon.png";
import ISA_academy_detail_background from "../../images/background/ISA_academy_detail_background.jpeg";

// Scroll animation - AOS
import Aos from "aos";
import "aos/dist/aos.css";

// CSS
import "../../styles/academy.css";

// import { academies } from "../../utilities/content";

// Axios and API
import { fetchWorkshopDetail } from "../../api/fetchWorkshopDetail";

// Moment.js
import moment from "moment";

// The page already has its own <h1>, so an <h1> written in the CMS content becomes an <h2> that looks the same
const parseContent = (html) =>
  parse(html, {
    replace: (node) => {
      if (node.type === "tag" && node.name === "h1") {
        return (
          <h2 {...attributesToProps(node.attribs)} className="h1">
            {domToReact(node.children)}
          </h2>
        );
      }
    },
  });

// "bit.ly/abc" -> "https://bit.ly/abc"
const withProtocol = (link) => (/^https?:\/\//i.test(link) ? link : `https://${link}`);

function useForceUpdate() {
  const [value, setValue] = useState(0);
  return () => setValue((value) => value + 1);
}

const DetailAcademy = () => {
  const forceUpdate = useForceUpdate();
  const { id } = useParams();

  useEffect(() => {
    Aos.init({
      duration: 1000,
    });
  }, []);

  const [workshop, setWorkshop] = useState(null);
  const [arrSchedules, setArrSchedules] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDetailWorkshop(id);
    }
  }, [id]);

  const fetchDetailWorkshop = async (workshopId) => {
    setNotFound(false);
    try {
      const result = await fetchWorkshopDetail(workshopId);
      if (result && result.status === 200 && result.data) {
        const { data = {}, data: { Schedules = [] } = {} } = result || {};
        const schedules =
          Schedules &&
          Array.isArray(Schedules) &&
          Schedules.length > 0 &&
          Schedules.map((schedule) => {
            return { ...schedule, isOpenInvestmentFee: false };
          });
        setArrSchedules(schedules);
        setWorkshop(data);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.log(err);
      setNotFound(true);
    }
  };

  // Event structured data, one event per schedule of the workshop
  const buildEvents = (workshop, schedules, name, description, path) => {
    const registerUrl = workshop.register_link
      ? withProtocol(workshop.register_link)
      : undefined;
    const isFree = workshop.type_of_workshop === "Free";
    const image = [workshop.main_image_url, workshop.cover_image_url]
      .filter(Boolean)
      .map((url) => absoluteUrl(url));

    return (Array.isArray(schedules) ? schedules : []).map((schedule) => {
      const mode = `${schedule.method_of_learning || ""} ${
        workshop.location || ""
      }`.toLowerCase();
      const hasOnline = mode.includes("online");
      const hasOffline = /offline|on-site|onsite|in-person/.test(mode);
      const attendanceMode =
        hasOnline && hasOffline
          ? "MixedEventAttendanceMode"
          : hasOnline
          ? "OnlineEventAttendanceMode"
          : "OfflineEventAttendanceMode";
      const virtualLocation = { "@type": "VirtualLocation", url: absoluteUrl(path) };
      const place = workshop.location
        ? {
            "@type": "Place",
            name: workshop.location,
            address: workshop.location,
          }
        : undefined;
      const location =
        attendanceMode === "OnlineEventAttendanceMode"
          ? virtualLocation
          : attendanceMode === "MixedEventAttendanceMode"
          ? [virtualLocation, place].filter(Boolean)
          : place;

      // The end time in the data is not reliable, so endDate is only set for multi-day workshops
      const start = isoDate(schedule.start_date);
      const end = isoDate(schedule.end_date);
      const endDate =
        start && end && start.slice(0, 10) !== end.slice(0, 10) ? end : undefined;

      const feeText = plainText(schedule.investment_fee, 400);
      const feeMatch = feeText.match(/IDR\s*([\d.,]+)/i);
      const price = isFree
        ? 0
        : feeMatch
        ? feeMatch[1].replace(/[.,]+$/, "").replace(/[.,]/g, "")
        : undefined;

      return {
        "@type": "Event",
        name,
        description,
        image,
        startDate: start,
        endDate,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: `https://schema.org/${attendanceMode}`,
        location,
        organizer: organizationRef,
        isAccessibleForFree: isFree ? true : undefined,
        offers:
          price !== undefined
            ? {
                "@type": "Offer",
                price: String(price),
                priceCurrency: "IDR",
                url: registerUrl || absoluteUrl(path),
                availability: "https://schema.org/InStock",
              }
            : undefined,
      };
    });
  };

  const handleOpenScheduleInvestmentFee = (index, key, value) => {
    const finalSchedules = arrSchedules;
    finalSchedules[index] = {
      ...finalSchedules[index],
      [`${key}`]: !value,
    };
    setArrSchedules(finalSchedules);
    forceUpdate();
  };

  if (workshop) {
    const {
      // AcademyWorkshopTags = [],
      Facilitators = [],
    } = workshop || {};
    const workshopTitle = plainText(workshop.workshop_title, 120);
    const workshopPath = `/academy/details/${id}`;
    const workshopDescription = plainText(
      workshop.input_summary || workshop.what_you_will_learn,
      160
    );
    const workshopImage = workshop.main_image_url || workshop.cover_image_url;
    return (
      <div>
        <Seo
          title={workshopTitle}
          description={workshopDescription}
          path={workshopPath}
          image={workshopImage}
          imageAlt={workshopTitle}
          breadcrumbs={[
            { name: "ISA Academy", path: "/academy" },
            { name: workshopTitle, path: workshopPath },
          ]}
          jsonLd={buildEvents(
            workshop,
            arrSchedules,
            workshopTitle,
            workshopDescription,
            workshopPath
          )}
        />
        <Header />
        <div className="academyDetailMainContainer">
          <div className="academyDetailImageContainer">
            <img
              src={ISA_academy_detail_background}
              alt=""
              className="academyDetailImageBackground"
            />
            <img
              src={workshop && workshop.main_image_url}
              alt={workshopTitle}
              className="academyDetailMainImage"
            />
          </div>
          {/* <img
            src={workshop && workshop.main_image_url}
            alt="banner"
            style={{
              maxHeight: "700px",
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          /> */}
          <ContainerText
            props={
              <>
                <div className="academyTitle headerDescription">
                  <h1 className="seoH1 seoH1Inherit">
                    {parse(workshop.workshop_title)}
                  </h1>
                </div>
                <div
                  style={{
                    textAlign: "left",
                    marginLeft: "1em",
                    marginRight: "1em",
                  }}
                >
                  <div className="eachPropertyAcademyContainer">
                    <div className="eachPropertyAcademy title">
                      What you will learn
                    </div>
                    <div className="eachPropertyAcademyDesc">
                      {workshop &&
                        workshop.what_you_will_learn &&
                        parseContent(workshop.what_you_will_learn)}
                    </div>
                  </div>
                  {arrSchedules &&
                  Array.isArray(arrSchedules) &&
                  arrSchedules.length > 0 ? (
                    <div className="eachPropertyAcademyContainer">
                      <div className="eachPropertyAcademy title">
                        Workshop Schedule
                      </div>
                      <div className="tableSchedule">
                        <table>
                          <thead>
                            <tr>
                              <th className="thSchedule">
                                <div>Schedule</div>
                              </th>
                              <th className="thDuration">Duration</th>
                              <th className="thLearningMethod">
                                Learning Method
                              </th>
                              <th className="thAction"></th>
                            </tr>
                          </thead>
                          {arrSchedules.map((schedule, index) => {
                            const {
                              id,
                              isOpenInvestmentFee,
                              investment_fee,
                              start_date,
                              end_date,
                              duration,
                              method_of_learning,
                            } = schedule || {};
                            return (
                              <tbody key={id}>
                                <tr className="adminTableRow">
                                  <td>
                                    <div className="tdScheduleValue">
                                      {moment(start_date).format(
                                        "DD MMMM YYYY"
                                      ) ===
                                      moment(end_date).format("DD MMMM YYYY")
                                        ? `${moment(start_date).format(
                                            "DD MMMM YYYY"
                                          )}`
                                        : `${moment(start_date).format(
                                            "DD"
                                          )}-${moment(end_date).format(
                                            "DD MMMM YYYY"
                                          )}`}
                                    </div>
                                  </td>
                                  <td className="tdDuration">
                                    <div>{duration}</div>
                                  </td>
                                  <td className="tdDuration">
                                    <div>{method_of_learning}</div>
                                  </td>
                                  <td>
                                    <div
                                      className="btnInvestmentFee"
                                      onClick={() =>
                                        handleOpenScheduleInvestmentFee(
                                          index,
                                          "isOpenInvestmentFee",
                                          isOpenInvestmentFee
                                        )
                                      }
                                    >
                                      <div>View Investment Fee</div>
                                      <div>
                                        <img
                                          src={
                                            isOpenInvestmentFee
                                              ? ArrowUpIcon
                                              : ArrowDownIcon
                                          }
                                          alt=""
                                          className="arrowUpDownIcon"
                                        ></img>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                {isOpenInvestmentFee ? (
                                  <tr
                                    className="adminTableRow"
                                    style={{ width: "100%" }}
                                  >
                                    <td>
                                      <div className="tdScheduleValue">
                                        {investment_fee &&
                                          parseContent(investment_fee)}
                                      </div>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                  </tr>
                                ) : null}
                              </tbody>
                            );
                          })}
                        </table>
                      </div>
                    </div>
                  ) : null}
                  {Facilitators &&
                  Array.isArray(Facilitators) &&
                  Facilitators.length > 0 ? (
                    <div className="eachPropertyAcademyContainer">
                      <div className="eachPropertyAcademy title">
                        Facilitator
                      </div>
                      {Facilitators.map((facilitator, index) => {
                        const {
                          facilitator_image_url,
                          facilitator_profile,
                          facilitator_name,
                        } = facilitator || {};
                        return (
                          <div key={facilitator.id}>
                            <ContainerText
                              props={
                                <div
                                  className="allFacilitatorsContainer"
                                  style={
                                    index === Facilitators.length - 1
                                      ? {
                                          marginLeft: "-1em",
                                          marginBottom: "1em",
                                        }
                                      : {
                                          marginLeft: "-1em",
                                          marginBottom: "4em",
                                        }
                                  }
                                >
                                  <div className="eachFacilitators">
                                    <div className="eachFacilitatorsDescContainer">
                                      <div className="eachMembersPicture">
                                        <img
                                          src={facilitator_image_url}
                                          alt={facilitator_name}
                                          loading="lazy"
                                        />
                                      </div>
                                      <div className="containerDesc">
                                        <div className="facilitatorName title">
                                          <p>{facilitator_name}</p>
                                        </div>
                                        <div
                                          className="eachMembersDesc paragrafDesc"
                                          style={{ marginTop: "20px" }}
                                        >
                                          <p>{facilitator_profile}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  <div className="eachPropertyAcademyContainer">
                    <div className="eachPropertyAcademy title">
                      Target Audience
                    </div>
                    <div className="eachPropertyAcademyDesc">
                      {workshop &&
                        workshop.target_audience &&
                        parseContent(workshop.target_audience)}
                    </div>
                  </div>
                  <div className="eachPropertyAcademyContainer">
                    <div className="eachPropertyAcademy title">
                      How to Register?
                    </div>
                    <div className="eachPropertyAcademyDesc">
                      {workshop &&
                        workshop.how_to_register &&
                        parseContent(workshop.how_to_register)}
                    </div>
                  </div>
                  {/* {AcademyWorkshopTags &&
                  Array.isArray(AcademyWorkshopTags) &&
                  AcademyWorkshopTags.length > 0 ? (
                    <div
                      className="eachPropertyAcademyContainer"
                      style={{ marginTop: "20px" }}
                    >
                      <div className="blogCardTags">Tags:</div>
                      {AcademyWorkshopTags.map((tag, index) => {
                        const { Tag: { tag_name } = {} } = tag;
                        return (
                          <div className="blogCardTagsBoxContainer" key={index}>
                            <div className="blogCardTagsBox">
                              <p>{tag_name}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null} */}
                  <div
                    className="contactFormSubmit"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignContent: "center",
                      justifyContent: "center",
                      marginBottom: "3em",
                    }}
                  >
                    <a
                      href={
                        workshop && workshop.register_link
                          ? withProtocol(workshop.register_link)
                          : undefined
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginRight: "3em" }}
                    >
                      <button className="btnRegister paragraf">
                        <img
                          src={RegisterIcon}
                          alt=""
                          style={{
                            marginRight: "0.5em",
                          }}
                        />
                        Register here
                      </button>
                    </a>
                    <a
                      href="https://api.whatsapp.com/send/?phone=6281218467661&text&app_absent=0"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <button className="btnWhatsapp paragraf">
                        <img
                          src={WhatsappIcon}
                          alt=""
                          style={{
                            marginRight: "0.5em",
                          }}
                        />
                        Whatsapp
                      </button>
                    </a>
                  </div>
                </div>
              </>
            }
          />
        </div>
        <Footer />
      </div>
    );
  } else {
    return notFound ? <Seo title="Workshop not found" noindex /> : null;
  }
};

export default DetailAcademy;
